import type { ApiResponse } from '@/types';

function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (!envUrl) return '/api';
  if (envUrl.endsWith('/api')) return envUrl;
  return envUrl.replace(/\/$/, '') + '/api';
}

const API_BASE_URL = getApiBaseUrl();

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem('auth_token');
  
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
  }
  
  const response = await fetch(`${API_BASE_URL}${endpoint}`, { ...options, headers });
  const data: ApiResponse<T> = await response.json();
  
  if (!response.ok || !data.success) {
    throw new Error(data.error || 'Unknown error');
  }
  
  return data.data as T;
}

// ============================================
// QUEST TYPES
// ============================================

export type QuestType = 'daily' | 'weekly' | 'story' | 'special';
export type QuestStatus = 'not_started' | 'in_progress' | 'completed' | 'claimed' | 'expired';

export interface Quest {
  id: string;
  questType: QuestType;
  title: string;
  description: string | null;
  requirements: {
    type: string;
    count: number;
    description?: string;
  };
  xpReward: number;
  achievementId: string | null;
  minLevel: number;
  maxLevel: number | null;
  sortOrder: number;
  userProgress?: {
    status: QuestStatus;
    currentProgress: number;
    completedAt: string | null;
    claimedAt: string | null;
  };
}

export interface UserQuest {
  id: string;
  questId: string;
  questTitle?: string;
  questType?: QuestType;
  status: QuestStatus;
  currentProgress: number;
  targetProgress?: number;
  startedAt: string;
  completedAt: string | null;
  claimedAt: string | null;
  expiresAt: string | null;
}

export interface ClaimRewardResult {
  xpGained: number;
  levelUp: boolean;
  newLevel: number;
  achievementUnlocked?: {
    id: string;
    name: string;
    description: string;
  };
}

export interface Achievement {
  id: string;
  name: string;
  description: string;
  emoji: string;
  unlockedAt?: string;
}

// ============================================
// API CALLS
// ============================================

export async function getAllQuests(): Promise<{
  daily: Quest[];
  weekly: Quest[];
  story: Quest[];
  special: Quest[];
}> {
  return request('/quests');
}

export async function getDailyQuests(): Promise<{ quests: Quest[] }> {
  return request('/quests/daily');
}

export async function getActiveQuests(): Promise<{ quests: UserQuest[] }> {
  return request('/quests/active');
}

export async function startQuest(questId: string): Promise<{ userQuest: UserQuest }> {
  return request(`/quests/${questId}/start`, { method: 'POST' });
}

export async function claimQuestReward(userQuestId: string): Promise<ClaimRewardResult> {
  return request(`/quests/${userQuestId}/claim`, { method: 'POST' });
}

export async function getAchievements(): Promise<{
  unlocked: Achievement[];
  locked: Achievement[];
}> {
  return request('/quests/achievements');
}
